import { FCWithChildren } from '@types';
import React, { useMemo, useState } from 'react';
import Select, { StylesConfig } from 'react-select';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToasts } from 'react-toast-notifications';

import { createNetwork } from 'sdk-apogee';

import { Button, InputSectionGroup } from '@components';
import { useThemeMode } from '@contexts/ThemeModeContext';
import { getReactSelectStyles } from '@utils';

import { ActionTypes, SEND_INVITE } from '../SharedList';

type CreateNetworkProps = {
  dispatch: (action: ActionTypes) => void;
};

type DurationOption = { label: string; value: number };

type ErrorType = { error?: string };

export const DURATION_OPTIONS: DurationOption[] = [
  { label: '2 Hours', value: 2 },
  { label: '8 Hours', value: 8 },
  { label: '24 Hours', value: 24 },
  { label: 'Unlimited', value: 0 },
];

const CreateNetwork: FCWithChildren<CreateNetworkProps> = ({ dispatch }) => {
  const { resolvedTheme } = useThemeMode();
  const { addToast } = useToasts();
  const queryClient = useQueryClient();
  const [duration, setDuration] = useState<DurationOption | null>(null);

  const selectStyles = useMemo(() => {
    const base = getReactSelectStyles(resolvedTheme);
    const accent = resolvedTheme === 'dark' ? '#93c5fd' : '#0075da';

    const inlineStyles: StylesConfig<any, boolean> = {
      ...base,
      control: (styles, state) => ({
        ...base.control?.(styles, state),
        backgroundColor: 'transparent',
        border: 0,
        boxShadow: 'none',
        minHeight: 'auto',
        '&:hover': { border: 0 },
      }),
      valueContainer: (styles) => ({ ...styles, padding: 0 }),
      input: (styles, state) => ({
        ...(base.input?.(styles, state) || styles),
        margin: 0,
        padding: 0,
      }),
      singleValue: (styles, state) => ({
        ...(base.singleValue?.(styles, state) || styles),
        color: accent,
        margin: 0,
      }),
      placeholder: (styles, state) => ({
        ...(base.placeholder?.(styles, state) || styles),
        color: accent,
        margin: 0,
      }),
      indicatorSeparator: () => ({ display: 'none' }),
      dropdownIndicator: (styles, state) => ({
        ...(base.dropdownIndicator?.(styles, state) || styles),
        padding: 0,
      }),
    };

    return inlineStyles;
  }, [resolvedTheme]);

  const { mutate: create, isPending } = useMutation({
    // `Unlimited` (0) means no expiration; everything else is sent in minutes.
    mutationFn: () => createNetwork(duration?.value ? duration.value * 60 : null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-network'] });
      addToast('You are a host of a network', { appearance: 'success' });
      dispatch({ type: SEND_INVITE });
    },
    onError: (apiErrors: ErrorType) => {
      addToast(apiErrors?.error || 'An error has occurred', { appearance: 'error' });
    },
  });

  return (
    <section className="w-full">
      <h2 className="text-black">Create Shared Network</h2>

      <p className="mt-2 text-black">
        Let friends join your network, so you can share devices such as printers, smartTVs, etc.
      </p>

      <div className="mt-6">
        <InputSectionGroup>
          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="network-duration">
                Duration
              </label>
              <div className="input-section-group__control">
                <Select
                  inputId="network-duration"
                  placeholder="Select duration"
                  options={DURATION_OPTIONS}
                  styles={selectStyles}
                  value={duration}
                  onChange={(option) => setDuration(option as DurationOption)}
                />
              </div>
            </div>
          </InputSectionGroup.Item>
        </InputSectionGroup>
      </div>

      <Button
        block
        className="mt-4 font-black"
        disabled={!duration || isPending}
        loading={isPending}
        onClick={() => create()}
      >
        Create
      </Button>
    </section>
  );
};

export default CreateNetwork;
